import * as React from 'react'
import { forwardRef, useCallback, useMemo } from 'react'

import {
  SavedViews,
  SavedViewsRef,
  type PresetTab,
} from '~/client/src/desktop/components/SavedViews'
import type { IDrillDownFilterCategory } from '~/client/src/shared/components/DrillDownFilter/DrillDownFilter.types'
import { LocalStorageKey } from '~/client/src/shared/enums/LocalStorageKey'
import type { IFilterItem } from '~/client/src/shared/lists/notifications/notificationListFilterCategories'
import {
  NotificationsListStore,
  NotificationTab,
} from '~/client/src/shared/lists/notifications/NotificationsList.store'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import { useNotificationsSavedViews } from '../hooks/useNotificationsSavedViews'

interface NotificationSavedViewsProps {
  notificationsListStore: NotificationsListStore
  projectId: string
  userId: string
  categories: IDrillDownFilterCategory<IFilterItem>[]
  onPresetTabSelect?: (tab: NotificationTab) => void
}

const NotificationSavedViews = forwardRef<
  SavedViewsRef,
  NotificationSavedViewsProps
>(
  (
    {
      notificationsListStore,
      projectId,
      userId,
      categories,
      onPresetTabSelect,
    },
    ref,
  ) => {
    const savedViewsAdapter = useNotificationsSavedViews({
      projectId,
      userId,
      notificationsListStore,
      categories,
    })

    const presetTabs: PresetTab[] = useMemo(
      () => [
        {
          id: NotificationTab.All,
          title: Localization.translator.all,
        },
        {
          id: NotificationTab.Important,
          title: Localization.translator.important,
          tooltip: Localization.translator.importantTabTooltip,
        },
        {
          id: NotificationTab.Updates,
          title: Localization.translator.updates,
          tooltip: Localization.translator.updatesTabTooltip,
        },
        {
          id: NotificationTab.Archive,
          title: Localization.translator.archive,
        },
      ],
      [],
    )

    const handlePresetTabSelect = useCallback(
      (tabId: string) => {
        onPresetTabSelect?.(tabId as NotificationTab)
      },
      [onPresetTabSelect],
    )

    return (
      <SavedViews
        key={projectId}
        ref={ref}
        views={savedViewsAdapter.savedViews}
        onViewSelect={savedViewsAdapter.handleApplyFilters}
        filterType="notifications"
        saveFilterMutation={savedViewsAdapter.saveFilterMutation}
        deleteFilterMutation={savedViewsAdapter.deleteFilterMutation}
        refetchQueryName="GetNotificationCustomFiltersByProject"
        appliedFilters={savedViewsAdapter.appliedFilters}
        facets={savedViewsAdapter.facets}
        groupingEnabled={false}
        localStorageKey={LocalStorageKey.NotificationSavedView}
        presetTabs={presetTabs}
        defaultPresetTabId={NotificationTab.Important}
        onPresetTabSelect={handlePresetTabSelect}
      />
    )
  },
)

export default NotificationSavedViews
